import { readBranding, writeBranding, clearBranding, sourceIdentity } from './dojoBrandingCache';
const denied = error => [401, 403].includes(error?.status || error?.response?.status);

// One generation owns every acquired object URL, cache transaction and callback.
export function startDojoBranding(owner, service, publish) {
  const controller = new AbortController();
  const signal = controller.signal;
  const urls = new Set();
  let current = { owner, status: 'pending' }, canonical, freshLogo = false, freshBackground = false, snapshotWritten = false;
  const emit = patch => { if (!signal.aborted) { current = { ...current, ...patch }; publish(current); } };
  const revoke = url => { if (url) { URL.revokeObjectURL?.(url); urls.delete(url); } };
  const own = blob => { const url = URL.createObjectURL(blob); urls.add(url); return url; };
  const purge = () => {
    urls.forEach(revoke);
    emit({ dojoId: null, dojoName: null, logoUrl: null, backgroundUrl: null, accent: null, focalPoint: null, status: 'ready' });
    void clearBranding(owner, signal);
  };
  publish(current);
  // Start both independently: cached bytes never wait for membership, and the
  // authenticated canonical read never waits for IndexedDB or image decoding.
  readBranding(owner, signal).then(value => {
    if (signal.aborted || !value || value.owner !== owner) return;
    if (canonical && (!canonical.dojoId || canonical.dojoId !== value.dojoId)) {
      void clearBranding(owner, signal);
      return;
    }
    // A late read can expose removed bytes even while another asset is held.
    // Do not erase a replacement snapshot already submitted by this generation.
    if (canonical && !canonical.background && value.backgroundBlob && !snapshotWritten) void clearBranding(owner, signal);
    emit({ dojoId: value.dojoId, dojoName: canonical?.dojoName ?? value.dojoName,
      accent: canonical ? canonical.accent : value.accent, focalPoint: canonical ? canonical.focalPoint : value.focalPoint,
      status: 'ready',
      // Hydrate each fallback independently; never displace acquired fresh bytes.
      logoUrl: freshLogo ? current.logoUrl : (!canonical || canonical.logo) && value.logoBlob ? own(value.logoBlob) : null,
      backgroundUrl: freshBackground ? current.backgroundUrl : (!canonical || canonical.background) && value.backgroundBlob ? own(value.backgroundBlob) : null });
  }).catch(() => {});
  const acquisitions = new Map();
  const acquire = async source => {
    if (!source || signal.aborted) return null;
    let blob = null;
    const url = await service.fetchDojoBrandingImage(source, { onBlob: value => { blob = value; } });
    if (signal.aborted) { URL.revokeObjectURL?.(url); return null; }
    urls.add(url);
    return { url, blob };
  };
  const load = source => {
    if (!source) return Promise.resolve(null);
    if (!acquisitions.has(source)) acquisitions.set(source, acquire(source));
    return acquisitions.get(source);
  };
  (async () => {
    try {
      const dojo = await service.getMyDojo();
      if (signal.aborted) return;
      if (!dojo?._id) { canonical = { dojoId: null }; purge(); return; }
      const branding = dojo.branding || {}, background = branding.background;
      canonical = { dojoId: dojo._id, dojoName: dojo.name,
        logo: branding.logo?.thumbnail_url || branding.logo?.image_url || dojo.image_url || null,
        background: background?.image_url || null,
        accent: /^#[0-9a-f]{6}$/i.test(branding.accent_color || '') ? branding.accent_color : null,
        focalPoint: background?.focal_point };
      if (current.dojoId && current.dojoId !== dojo._id) purge();
      // Deletion is authoritative even while the replacement image is pending.
      if (!canonical.logo) { revoke(current.logoUrl); void clearBranding(owner, signal); }
      if (!canonical.background) {
        if (current.backgroundUrl) void clearBranding(owner, signal);
        revoke(current.backgroundUrl);
      }
      emit({ dojoId: canonical.dojoId, dojoName: canonical.dojoName, accent: canonical.accent, focalPoint: canonical.focalPoint,
        logoUrl: canonical.logo ? current.logoUrl : null, backgroundUrl: canonical.background ? current.backgroundUrl : null,
        status: current.logoUrl || !canonical.logo ? 'ready' : 'pending' });
      const snapshot = { owner, dojoId: canonical.dojoId, dojoName: canonical.dojoName, accent: canonical.accent, focalPoint: canonical.focalPoint,
        logoSource: await sourceIdentity(canonical.logo), backgroundSource: await sourceIdentity(canonical.background),
        logoBlob: null, backgroundBlob: null };
      if (signal.aborted) return;
      let logo, bg, complete = true;
      try { logo = await load(canonical.logo); }
      catch (error) { if (denied(error)) { canonical = { dojoId: null }; purge(); return; } complete = false; }
      if (signal.aborted) return;
      if (logo) { freshLogo = true; revoke(current.logoUrl); emit({ logoUrl: logo.url }); snapshot.logoBlob = logo.blob; }
      emit({ status: 'ready' });
      try { bg = await load(canonical.background); }
      catch (error) { if (denied(error)) { canonical = { dojoId: null }; purge(); return; } complete = false; }
      if (signal.aborted) return;
      if (bg) { freshBackground = true; revoke(current.backgroundUrl); emit({ backgroundUrl: bg.url }); snapshot.backgroundBlob = bg.blob; }
      // A transient partial refresh never overwrites the last successful snapshot.
      // Transport callback gives us the exact authenticated response bytes, no
      // duplicate fetch of the private source and no persisted object URL.
      if (complete && (!canonical.logo || snapshot.logoBlob) && (!canonical.background || snapshot.backgroundBlob)) {
        snapshotWritten = true;
        void writeBranding(snapshot, signal);
      }
    } catch (error) {
      if (signal.aborted) return;
      if (denied(error)) { canonical = { dojoId: null }; purge(); }
      else emit({ status: 'ready' });
    }
  })();
  return () => { controller.abort(); urls.forEach(revoke); };
}
